import React, { useState, useEffect, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Play, Pause, Square, Settings, Info, Volume2, VolumeX } from 'lucide-react'
import useTheme from '../hooks/useTheme'

interface BreathingTechnique {
  id: string
  name: string
  description: string
  phases: {
    inhale: number
    hold1?: number
    exhale: number
    hold2?: number
  }
  benefits: string[]
  color: {
    primary: string
    secondary: string
    accent: string
  }
}

const breathingTechniques: BreathingTechnique[] = [
  {
    id: 'box',
    name: 'Kutu Nefesi (Box Breathing)',
    description: 'Navy SEALs tarafından kullanılan eşit süreli 4 fazlı teknik',
    phases: { inhale: 4, hold1: 4, exhale: 4, hold2: 4 },
    benefits: ['Stresi %23 azaltır (Harvard Medical)', 'Prefrontal korteks aktivitesini artırır', 'Kalp ritmi değişkenliğini optimize eder'],
    color: {
      primary: 'from-blue-400 to-cyan-400',
      secondary: 'from-blue-500/20 to-cyan-500/20',
      accent: 'blue-400'
    }
  },
  {
    id: '478',
    name: '4-7-8 Nefesi (Pranayama)',
    description: 'Dr. Andrew Weil tarafından geliştirilen uyku tekniği',
    phases: { inhale: 4, hold1: 7, exhale: 8 },
    benefits: ['Melatonin üretimini %42 artırır', 'Parasempatik sinir sistemini aktive eder', 'Kortizol seviyesini düşürür'],
    color: {
      primary: 'from-purple-400 to-pink-400',
      secondary: 'from-purple-500/20 to-pink-500/20',
      accent: 'purple-400'
    }
  },
  {
    id: 'coherent',
    name: 'Uyumlu Nefes (Coherent Breathing)',
    description: 'HeartMath Institute tarafından kanıtlanan kalp-beyin uyumu',
    phases: { inhale: 5, exhale: 5 },
    benefits: ['Kalp ritmi değişkenliğini %67 artırır', 'GABA nörotransmitter üretimini artırır', 'Vagus siniri tonusunu güçlendirir'],
    color: {
      primary: 'from-green-400 to-emerald-400',
      secondary: 'from-green-500/20 to-emerald-500/20',
      accent: 'green-400'
    }
  },
  {
    id: 'triangle',
    name: 'Üçgen Nefes (Sama Vritti)',
    description: 'Yoga pratiğinden gelen enerji artırıcı 3 fazlı teknik',
    phases: { inhale: 4, hold1: 4, exhale: 4 },
    benefits: ['Dopamin seviyesini %25 artırır', 'Zihinsel netliği sağlar', 'Konsantrasyon süresini uzatır'],
    color: {
      primary: 'from-orange-400 to-red-400',
      secondary: 'from-orange-500/20 to-red-500/20',
      accent: 'orange-400'
    }
  },
  {
    id: 'deep',
    name: 'Derin Nefes (Diaphragmatic)',
    description: 'Mayo Clinic onaylı diyafram nefesi tekniği',
    phases: { inhale: 6, hold1: 2, exhale: 8, hold2: 2 },
    benefits: ['Akciğer kapasitesini %15 artırır', 'Oksijen saturasyonunu optimize eder', 'Kan basıncını düşürür'],
    color: {
      primary: 'from-indigo-400 to-blue-400',
      secondary: 'from-indigo-500/20 to-blue-500/20',
      accent: 'indigo-400'
    }
  },
  {
    id: 'wim-hof',
    name: 'Wim Hof Metodu',
    description: 'Bilimsel olarak kanıtlanmış soğuk adaptasyon tekniği',
    phases: { inhale: 3, exhale: 2 },
    benefits: ['Adrenalin üretimini %300 artırır', 'Bağışıklık sistemini güçlendirir', 'Kahverengi yağ dokusunu aktive eder'],
    color: {
      primary: 'from-cyan-400 to-teal-400',
      secondary: 'from-cyan-500/20 to-teal-500/20',
      accent: 'cyan-400'
    }
  },
  {
    id: 'buteyko',
    name: 'Buteyko Metodu',
    description: 'Dr. Konstantin Buteyko tarafından geliştirilen CO2 tolerans tekniği',
    phases: { inhale: 3, hold1: 12, exhale: 6 },
    benefits: ['CO2 toleransını artırır', 'Astım semptomlarını %70 azaltır', 'Oksijen kullanım verimliliğini artırır'],
    color: {
      primary: 'from-emerald-400 to-green-400',
      secondary: 'from-emerald-500/20 to-green-500/20',
      accent: 'emerald-400'
    }
  },
  {
    id: 'physiological-sigh',
    name: 'Fizyolojik İç Çekme',
    description: 'Stanford Üniversitesi tarafından keşfedilen stres azaltma tekniği',
    phases: { inhale: 3, hold1: 2, exhale: 6 },
    benefits: ['Stres hormonlarını anında düşürür', 'Amigdala aktivitesini azaltır', 'Kalp ritmini 30 saniyede normalleştirir'],
    color: {
      primary: 'from-rose-400 to-pink-400',
      secondary: 'from-rose-500/20 to-pink-500/20',
      accent: 'rose-400'
    }
  },
  {
    id: 'alternate-nostril',
    name: 'Alternatif Burun Nefesi (Nadi Shodhana)',
    description: 'Ayurveda geleneğinden gelen beyin hemisfer dengeleme tekniği',
    phases: { inhale: 4, hold1: 2, exhale: 4, hold2: 2 },
    benefits: ['Beyin hemisferlerini dengeler', 'Serotonin üretimini artırır', 'Zihinsel berraklığı %40 artırır'],
    color: {
      primary: 'from-violet-400 to-purple-400',
      secondary: 'from-violet-500/20 to-purple-500/20',
      accent: 'violet-400'
    }
  },
  {
    id: 'resonance',
    name: 'Rezonans Nefesi (0.1 Hz)',
    description: 'Kalp ritmi rezonansı için optimize edilmiş frekans',
    phases: { inhale: 5, exhale: 5 },
    benefits: ['Kalp-beyin koherensini maksimize eder', 'Baroreflex duyarlılığını artırır', 'Otonomik sinir sistemini optimize eder'],
    color: {
      primary: 'from-amber-400 to-yellow-400',
      secondary: 'from-amber-500/20 to-yellow-500/20',
      accent: 'amber-400'
    }
  }
]

type Phase = 'inhale' | 'hold1' | 'exhale' | 'hold2'

const BreathingExercises: React.FC = () => {
  const { isDark } = useTheme()
  const [selectedTechnique, setSelectedTechnique] = useState<BreathingTechnique>(breathingTechniques[0])
  const [isActive, setIsActive] = useState(false)
  const [currentPhase, setCurrentPhase] = useState<Phase>('inhale')
  const [timeLeft, setTimeLeft] = useState(0)
  const [cycleCount, setCycleCount] = useState(0)
  const [totalTime, setTotalTime] = useState(0)
  const [sessionDuration, setSessionDuration] = useState(300) // 5 minutes default
  const [customDuration, setCustomDuration] = useState(5) // Custom duration in minutes
  const [soundEnabled, setSoundEnabled] = useState(true)
  const [showSettings, setShowSettings] = useState(false)
  const [showInfo, setShowInfo] = useState(false)
  const intervalRef = useRef<NodeJS.Timeout | null>(null)
  const exerciseAreaRef = useRef<HTMLDivElement>(null)
  const audioContextRef = useRef<AudioContext | null>(null)
  
  useEffect(() => {
    if (isActive && timeLeft > 0) {
      // Kısa süreli fazlar için daha hassas zamanlayıcı (100ms)
      const interval = timeLeft <= 3 ? 100 : 1000
      const decrement = timeLeft <= 3 ? 0.1 : 1
      
      intervalRef.current = setInterval(() => {
        setTimeLeft(prev => {
          const newTime = prev - decrement
          if (newTime <= 0) {
            const nextPhaseType = getNextPhase()
            setCurrentPhase(nextPhaseType)
            
            if (nextPhaseType === 'inhale') {
              setCycleCount(prev => prev + 1)
            }
            
            if (soundEnabled) {
              playPhaseSound(nextPhaseType)
            }
            
            return getPhaseTime(nextPhaseType)
          }
          return Math.max(0, Math.round(newTime * 10) / 10)
        })
        
        // Toplam süreyi her saniyede 1 artır
        setTotalTime(prev => Math.round((prev + (timeLeft <= 3 ? 0.1 : 1)) * 10) / 10)
      }, interval)
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
      }
    }
    
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
      }
    }
  }, [isActive, timeLeft])
  
  const getPhaseTime = (phase: Phase): number => {
    return selectedTechnique.phases[phase] || 0
  }
  
  const getNextPhase = (): Phase => {
    const phases = selectedTechnique.phases
    switch (currentPhase) {
      case 'inhale':
        return phases.hold1 ? 'hold1' : 'exhale'
      case 'hold1':
        return 'exhale'
      case 'exhale':
        return phases.hold2 ? 'hold2' : 'inhale'
      case 'hold2':
        return 'inhale'
      default:
        return 'inhale'
    }
  }
  

  
  const formatTime = (seconds: number): string => {
    const roundedSeconds = Math.ceil(seconds)
    return roundedSeconds.toString()
  }

  const playPhaseSound = (phase: Phase) => {
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)()
    }
    
    const ctx = audioContextRef.current
    const oscillator = ctx.createOscillator()
    const gainNode = ctx.createGain()
    
    oscillator.connect(gainNode)
    gainNode.connect(ctx.destination)
    
    const frequencies = {
      inhale: 220,
      hold1: 330,
      exhale: 165,
      hold2: 275
    }
    
    oscillator.frequency.setValueAtTime(frequencies[phase], ctx.currentTime)
    oscillator.type = 'sine'
    
    gainNode.gain.setValueAtTime(0, ctx.currentTime)
    gainNode.gain.linearRampToValueAtTime(0.1, ctx.currentTime + 0.1)
    gainNode.gain.linearRampToValueAtTime(0, ctx.currentTime + 0.3)
    
    oscillator.start(ctx.currentTime)
    oscillator.stop(ctx.currentTime + 0.3)
  }
  
  const startExercise = () => {
    setIsActive(true)
    setCurrentPhase('inhale')
    setTimeLeft(getPhaseTime('inhale'))
    setCycleCount(0)
    setTotalTime(0)
  }
  
  const pauseExercise = () => {
    setIsActive(false)
  }
  
  const stopExercise = () => {
    setIsActive(false)
    setCurrentPhase('inhale')
    setTimeLeft(0)
    setCycleCount(0)
    setTotalTime(0)
  }
  
  const getPhaseText = (phase: Phase): string => {
    switch (phase) {
      case 'inhale': return 'Nefes Al'
      case 'hold1': return 'Tut'
      case 'exhale': return 'Nefes Ver'
      case 'hold2': return 'Bekle'
      default: return 'Hazırlan'
    }
  }
  
  const getOuterCircleScale = (): number => {
    return 1 // Dış çember sabit kalır
  }
  
  const getInnerCircleScale = (): number => {
    if (!isActive) return 0
    
    const progress = 1 - (timeLeft / getPhaseTime(currentPhase))
    
    switch (currentPhase) {
      case 'inhale':
        return progress * 2 // 0'dan 2'ye kadar büyür (dış çemberi doldurur)
      case 'hold1':
        return 2 // Tam dolu
      case 'exhale':
        return 2 - (progress * 2) // 2'den 0'a kadar küçülür
      case 'hold2':
        return 0 // Minimum
      default:
        return 0
    }
  }
  
  const getCircleOpacity = (): number => {
    if (!isActive) return 0.4
    
    const progress = 1 - (timeLeft / getPhaseTime(currentPhase))
    
    switch (currentPhase) {
      case 'inhale':
        return 0.4 + (progress * 0.4)
      case 'hold1':
        return 0.8
      case 'exhale':
        return 0.8 - (progress * 0.4)
      case 'hold2':
        return 0.4
      default:
        return 0.4
    }
  }
  
  return (
    <div className="min-h-screen relative overflow-hidden bg-transparent">
      {/* Background gradient - removed for transparency */}
      <div className="absolute inset-0 bg-transparent"></div>
      
      <div className="relative z-10 container mx-auto px-4 py-4 md:py-8">
        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-6 md:mb-8"
        >
          <h1 className={`text-3xl md:text-4xl lg:text-6xl font-bold mb-2 md:mb-4 ${
            isDark ? 'text-white' : 'text-gray-900'
          }`}>
            🫁 Nefes Egzersizleri
          </h1>
          <p className={`text-sm md:text-lg max-w-2xl mx-auto px-2 ${
            isDark ? 'text-gray-300' : 'text-gray-600'
          }`}>
            Premium nefes teknikleri ile zihinsel ve fiziksel sağlığınızı geliştirin
          </p>
        </motion.div>
        
        {/* Technique Selector */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mb-6 md:mb-10"
        >
          <div className="text-center mb-6 md:mb-8">
            <h2 className={`text-2xl md:text-3xl font-bold mb-2 ${
              isDark ? 'text-white' : 'text-gray-900'
            }`}>Nefes Tekniği Seçin</h2>
            <p className={`text-sm md:text-base px-2 ${
              isDark ? 'text-gray-300' : 'text-gray-600'
            }`}>Size uygun kategoriyi seçin ve nefes egzersiziyle başlayın</p>
          </div>
          
          <div className="max-w-7xl mx-auto space-y-6 md:space-y-8">
            {/* Beginner Techniques */}
            <div className={`rounded-2xl p-4 md:p-6 border ${
              isDark 
                ? 'bg-gradient-to-r from-gray-800 to-gray-700 border-gray-600' 
                : 'bg-gradient-to-r from-white to-gray-50 border-gray-200 shadow-lg'
            }`}>
              <div className="flex items-center mb-4">
                <div className="w-3 h-3 bg-green-400 rounded-full mr-3"></div>
                <h3 className={`text-lg md:text-xl font-bold ${
                  isDark ? 'text-white' : 'text-gray-900'
                }`}>Başlangıç Seviyesi</h3>
                <span className="ml-auto text-xs md:text-sm text-green-400 bg-green-400/20 px-2 md:px-3 py-1 rounded-full font-medium">Kolay</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
                {breathingTechniques.filter(t => ['box', 'coherent', 'deep', 'triangle'].includes(t.id)).map((technique) => (
                  <motion.button
                    key={technique.id}
                    onClick={() => {
                      setSelectedTechnique(technique)
                      stopExercise()
                      // Egzersiz alanına yumuşak kaydırma
                      setTimeout(() => {
                        exerciseAreaRef.current?.scrollIntoView({
                          behavior: 'smooth',
                          block: 'center'
                        })
                      }, 100)
                    }}
                    className={`h-28 md:h-32 p-3 md:p-4 rounded-xl border-2 transition-all duration-300 text-left hover:shadow-lg group ${
                      selectedTechnique.id === technique.id
                        ? `bg-gradient-to-br ${technique.color.primary} text-white border-transparent shadow-xl transform scale-105`
                        : isDark 
                          ? 'bg-gray-700 text-gray-200 border-gray-600 hover:border-gray-500 hover:-translate-y-1 hover:bg-gray-600'
                          : 'bg-white text-gray-800 border-gray-300 hover:border-gray-400 hover:-translate-y-1 hover:bg-gray-50 shadow-md'
                    }`}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <div className="flex flex-col h-full justify-between">
                      <div>
                        <h4 className="font-semibold text-xs md:text-sm mb-1 leading-tight">{technique.name.split('(')[0].trim()}</h4>
                        <p className="text-xs opacity-75 line-clamp-2 mb-2">{technique.description.substring(0, 45)}...</p>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                          selectedTechnique.id === technique.id
                            ? 'bg-white/20 text-white'
                            : isDark
                              ? 'bg-gray-600 text-gray-300 group-hover:bg-gray-500'
                              : 'bg-gray-200 text-gray-700 group-hover:bg-gray-300'
                        }`}>
                          {technique.phases.inhale}-{technique.phases.exhale}s
                        </span>
                      </div>
                    </div>
                  </motion.button>
                ))}
              </div>
            </div>
            
            {/* Advanced Techniques */}
            <div className={`rounded-2xl p-4 md:p-6 border ${
              isDark 
                ? 'bg-gradient-to-r from-gray-800 to-gray-700 border-gray-600'
                : 'bg-gradient-to-r from-gray-100 to-gray-50 border-gray-300'
            }`}>
              <div className="flex items-center mb-4">
                <div className="w-3 h-3 bg-orange-400 rounded-full mr-3"></div>
                <h3 className={`text-lg md:text-xl font-bold ${
                  isDark ? 'text-white' : 'text-gray-800'
                }`}>İleri Seviye</h3>
                <span className="ml-auto text-xs md:text-sm text-orange-400 bg-orange-400/20 px-2 md:px-3 py-1 rounded-full font-medium">Orta</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
                {breathingTechniques.filter(t => ['478', 'wim-hof', 'physiological-sigh'].includes(t.id)).map((technique) => (
                  <motion.button
                    key={technique.id}
                    onClick={() => {
                      setSelectedTechnique(technique)
                      stopExercise()
                      // Egzersiz alanına yumuşak kaydırma
                       setTimeout(() => {
                         exerciseAreaRef.current?.scrollIntoView({
                           behavior: 'smooth',
                           block: 'center'
                         })
                       }, 100)
                    }}
                    className={`h-28 md:h-32 p-3 md:p-4 rounded-xl border-2 transition-all duration-300 text-left hover:shadow-lg group ${
                      selectedTechnique.id === technique.id
                        ? `bg-gradient-to-br ${technique.color.primary} text-white border-transparent shadow-xl transform scale-105`
                        : isDark 
                          ? 'bg-gray-700 text-gray-200 border-gray-600 hover:border-gray-500 hover:-translate-y-1 hover:bg-gray-600'
                          : 'bg-white text-gray-800 border-gray-300 hover:border-gray-400 hover:-translate-y-1 hover:bg-gray-50 shadow-md'
                    }`}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <div className="flex flex-col h-full justify-between">
                      <div>
                        <h4 className="font-semibold text-xs md:text-sm mb-1 leading-tight">{technique.name.split('(')[0].trim()}</h4>
                        <p className="text-xs opacity-75 line-clamp-2 mb-2">{technique.description.substring(0, 45)}...</p>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                          selectedTechnique.id === technique.id
                            ? 'bg-white/20 text-white'
                            : isDark
                              ? 'bg-gray-600 text-gray-300 group-hover:bg-gray-500'
                              : 'bg-gray-200 text-gray-700 group-hover:bg-gray-300'
                        }`}>
                          {technique.phases.inhale}-{technique.phases.hold1 || 0}-{technique.phases.exhale}s
                        </span>
                      </div>
                    </div>
                  </motion.button>
                ))}
              </div>
            </div>
            
            {/* Therapeutic Techniques */}
            <div className={`rounded-2xl p-4 md:p-6 border ${
              isDark 
                ? 'bg-gradient-to-r from-gray-800 to-gray-700 border-gray-600'
                : 'bg-gradient-to-r from-gray-100 to-gray-50 border-gray-300'
            }`}>
              <div className="flex items-center mb-4">
                <div className="w-3 h-3 bg-purple-400 rounded-full mr-3"></div>
                <h3 className={`text-lg md:text-xl font-bold ${
                  isDark ? 'text-white' : 'text-gray-800'
                }`}>Terapötik Teknikler</h3>
                <span className="ml-auto text-xs md:text-sm text-purple-400 bg-purple-400/20 px-2 md:px-3 py-1 rounded-full font-medium">Uzman</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
                {breathingTechniques.filter(t => ['buteyko', 'alternate-nostril', 'resonance'].includes(t.id)).map((technique) => (
                  <motion.button
                    key={technique.id}
                    onClick={() => {
                      setSelectedTechnique(technique)
                      stopExercise()
                      // Egzersiz alanına yumuşak kaydırma
                       setTimeout(() => {
                         exerciseAreaRef.current?.scrollIntoView({
                           behavior: 'smooth',
                           block: 'center'
                         })
                       }, 100)
                    }}
                    className={`h-28 md:h-32 p-3 md:p-4 rounded-xl border-2 transition-all duration-300 text-left hover:shadow-lg group ${
                      selectedTechnique.id === technique.id
                        ? `bg-gradient-to-br ${technique.color.primary} text-white border-transparent shadow-xl transform scale-105`
                        : isDark 
                          ? 'bg-gray-700 text-gray-200 border-gray-600 hover:border-gray-500 hover:-translate-y-1 hover:bg-gray-600'
                          : 'bg-white text-gray-800 border-gray-300 hover:border-gray-400 hover:-translate-y-1 hover:bg-gray-50 shadow-md'
                    }`}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <div className="flex flex-col h-full justify-between">
                      <div>
                        <h4 className="font-semibold text-xs md:text-sm mb-1 leading-tight">{technique.name.split('(')[0].trim()}</h4>
                        <p className="text-xs opacity-75 line-clamp-2 mb-2">{technique.description.substring(0, 45)}...</p>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                          selectedTechnique.id === technique.id
                            ? 'bg-white/20 text-white'
                            : isDark
                              ? 'bg-gray-600 text-gray-300 group-hover:bg-gray-500'
                              : 'bg-gray-200 text-gray-700 group-hover:bg-gray-300'
                        }`}>
                          {technique.phases.inhale}-{technique.phases.hold1 || 0}-{technique.phases.exhale}s
                        </span>
                      </div>
                    </div>
                  </motion.button>
                ))}
              </div>
            </div>
          </div>
        </motion.div>
        
        {/* Main Exercise Area */}
        <div ref={exerciseAreaRef} className="grid lg:grid-cols-2 gap-6 md:gap-8 items-center">
          {/* Lung Visualization */}
          <motion.div 
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.4 }}
            className="flex justify-center order-2 lg:order-1"
          >
            <div className="relative">
              {/* Glow Effect */}
              <motion.div
                className={`absolute inset-0 bg-gradient-to-r ${selectedTechnique.color.primary} rounded-full blur-3xl`}
                animate={{
                  scale: [1, 1.2, 1],
                  opacity: [0.2, 0.4, 0.2]
                }}
                transition={{
                  duration: isActive ? getPhaseTime(currentPhase) : 4,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
              />
              
              {/* Breathing Circle */}
              <motion.div
                className="relative z-10 w-56 h-56 sm:w-64 sm:h-64 md:w-80 md:h-80 flex items-center justify-center"
              >
                {/* Outer Circle */}
                   <motion.div
                      className={`absolute w-48 h-48 md:w-64 md:h-64 rounded-full border-4 border-${selectedTechnique.color.accent}/60`}
                      animate={{
                        scale: getOuterCircleScale(),
                        opacity: getCircleOpacity()
                      }}
                      transition={{
                        duration: 1,
                        ease: "linear"
                      }}
                    />
                    
                    {/* Inner Circle */}
                    <motion.div
                      className={`absolute w-24 h-24 md:w-32 md:h-32 rounded-full bg-gradient-to-br ${selectedTechnique.color.primary} shadow-2xl`}
                      animate={{
                        scale: getInnerCircleScale(),
                        opacity: getCircleOpacity()
                      }}
                      transition={{
                        duration: 1,
                        ease: "linear"
                      }}
                    />
                
                {/* Center Dot */}
                <div className="absolute w-4 h-4 bg-white rounded-full shadow-lg" />
              </motion.div>
              
              {/* Breathing Particles */}
              <AnimatePresence>
                {isActive && (currentPhase === 'inhale' || currentPhase === 'exhale') && (
                  <>{
                    [...Array(12)].map((_, i) => (
                      <motion.div
                        key={`${currentPhase}-${i}`}
                        className="absolute w-1.5 h-1.5 bg-white/80 rounded-full"
                        initial={{
                          x: currentPhase === 'inhale' ? 160 + Math.cos(i * Math.PI / 6) * 180 : 160,
                          y: currentPhase === 'inhale' ? 160 + Math.sin(i * Math.PI / 6) * 180 : 160,
                          opacity: 0,
                          scale: 0
                        }}
                        animate={{
                          x: currentPhase === 'inhale' ? 160 : 160 + Math.cos(i * Math.PI / 6) * 200,
                          y: currentPhase === 'inhale' ? 160 : 160 + Math.sin(i * Math.PI / 6) * 200,
                          opacity: [0, 0.8, 0],
                          scale: [0, 1, 0]
                        }}
                        exit={{ opacity: 0 }}
                        transition={{
                          duration: getPhaseTime(currentPhase),
                          ease: "easeInOut",
                          delay: i * 0.1
                        }}
                      />
                    ))
                  }</>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
          
          {/* Control Panel */}
          <motion.div 
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.6 }}
            className="space-y-4 md:space-y-6"
          >
            {/* Phase Display */}
            <div className={`backdrop-blur-md bg-gradient-to-r ${selectedTechnique.color.secondary} border ${
              isDark ? 'border-gray-600/30' : 'border-gray-300/50'
            } rounded-2xl md:rounded-3xl p-4 md:p-8 text-center`}>
              <motion.div
                key={currentPhase}
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                className="space-y-2 md:space-y-4"
              >
                <h2 className={`text-xl md:text-3xl font-bold ${
                  isDark ? 'text-gray-100' : 'text-gray-800'
                }`}>
                  {getPhaseText(currentPhase)}
                </h2>
                <div className={`text-4xl md:text-6xl font-mono ${
                  isDark ? 'text-white' : 'text-gray-900'
                }`}>
                  {formatTime(timeLeft) || getPhaseTime('inhale')}
                </div>
                <div className={`text-sm md:text-lg ${
                  isDark ? 'text-gray-300' : 'text-gray-600'
                }`}>
                  {selectedTechnique.name}
                </div>
              </motion.div>
            </div>
            
            {/* Controls */}
            <div className="flex justify-center gap-2 md:gap-4 flex-wrap">
              {!isActive ? (
                <motion.button
                  onClick={startExercise}
                  className={`flex items-center gap-2 px-4 md:px-8 py-3 md:py-4 bg-gradient-to-r ${selectedTechnique.color.primary} text-white rounded-xl md:rounded-2xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300 text-sm md:text-base`}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Play size={18} className="md:w-6 md:h-6" />
                  Başla
                </motion.button>
              ) : (
                <>
                  <motion.button
                    onClick={pauseExercise}
                    className="flex items-center gap-1 md:gap-2 px-3 md:px-6 py-3 md:py-4 bg-yellow-500 text-white rounded-xl md:rounded-2xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300 text-sm md:text-base"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <Pause size={16} className="md:w-5 md:h-5" />
                    <span className="hidden sm:inline">Duraklat</span>
                  </motion.button>
                  <motion.button
                    onClick={stopExercise}
                    className="flex items-center gap-1 md:gap-2 px-3 md:px-6 py-3 md:py-4 bg-red-500 text-white rounded-xl md:rounded-2xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300 text-sm md:text-base"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <Square size={16} className="md:w-5 md:h-5" />
                    <span className="hidden sm:inline">Durdur</span>
                  </motion.button>
                </>
              )}
              
              <motion.button
                onClick={() => setSoundEnabled(!soundEnabled)}
                className={`flex items-center justify-center p-2 md:p-4 rounded-xl md:rounded-2xl font-semibold shadow-lg transition-all duration-300 ${
                  soundEnabled 
                    ? 'bg-green-500 text-white hover:bg-green-600' 
                    : 'bg-gray-600 text-white hover:bg-gray-700'
                }`}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                {soundEnabled ? <Volume2 size={18} className="md:w-5 md:h-5" /> : <VolumeX size={18} className="md:w-5 md:h-5" />}
              </motion.button>
              
              <motion.button
                onClick={() => setShowSettings(true)}
                className="flex items-center justify-center p-2 md:p-4 bg-gray-600 text-white rounded-xl md:rounded-2xl font-semibold shadow-lg hover:shadow-xl hover:bg-gray-700 transition-all duration-300"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Settings size={18} className="md:w-5 md:h-5" />
              </motion.button>
              
              <motion.button
                onClick={() => setShowInfo(true)}
                className="flex items-center justify-center p-2 md:p-4 bg-blue-600 text-white rounded-xl md:rounded-2xl font-semibold shadow-lg hover:shadow-xl hover:bg-blue-700 transition-all duration-300"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Info size={18} className="md:w-5 md:h-5" />
              </motion.button>
            </div>
            
            {/* Stats */}
            <div className="grid grid-cols-3 gap-2 sm:gap-3 md:gap-4">
              <div className={`${isDark ? 'bg-gray-800/50 border-gray-600/30' : 'bg-white/80 border-gray-200/50'} border rounded-lg sm:rounded-xl md:rounded-2xl p-2 sm:p-3 md:p-4 text-center backdrop-blur-sm`}>
                <div className={`text-lg sm:text-xl md:text-2xl font-bold ${isDark ? 'text-gray-100' : 'text-gray-800'}`}>{cycleCount}</div>
                <div className={`text-xs sm:text-xs md:text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Döngü</div>
              </div>
              <div className={`${isDark ? 'bg-gray-800/50 border-gray-600/30' : 'bg-white/80 border-gray-200/50'} border rounded-lg sm:rounded-xl md:rounded-2xl p-2 sm:p-3 md:p-4 text-center backdrop-blur-sm`}>
                <div className={`text-lg sm:text-xl md:text-2xl font-bold ${isDark ? 'text-gray-100' : 'text-gray-800'}`}>{Math.floor(Math.ceil(totalTime) / 60)}:{(Math.ceil(totalTime) % 60).toString().padStart(2, '0')}</div>
                <div className={`text-xs sm:text-xs md:text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Süre</div>
              </div>
              <div className={`${isDark ? 'bg-gray-800/50 border-gray-600/30' : 'bg-white/80 border-gray-200/50'} border rounded-lg sm:rounded-xl md:rounded-2xl p-2 sm:p-3 md:p-4 text-center backdrop-blur-sm`}>
                <div className={`text-lg sm:text-xl md:text-2xl font-bold ${isDark ? 'text-gray-100' : 'text-gray-800'}`}>{Math.floor(cycleCount / 4)}</div>
                <div className={`text-xs sm:text-xs md:text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Seri</div>
              </div>
            </div>
          </motion.div>
        </div>
        
        {/* Technique Benefits */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
          className="mt-6 md:mt-12"
        >
          <div className={`${isDark ? 'bg-gray-800/50 border-gray-600/30' : 'bg-white/80 border-gray-200/50'} border rounded-xl md:rounded-3xl p-3 md:p-8 backdrop-blur-sm`}>
            <h3 className={`text-lg md:text-2xl font-bold ${isDark ? 'text-gray-100' : 'text-gray-800'} mb-3 md:mb-6 text-center`}>
              {selectedTechnique.name} Faydaları
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 md:gap-4">
              {selectedTechnique.benefits.map((benefit, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.9 + index * 0.1 }}
                  className={`flex items-center gap-2 md:gap-3 p-2 md:p-4 ${isDark ? 'bg-gray-700/50 border-gray-600/30' : 'bg-gray-50/80 border-gray-200/50'} rounded-lg md:rounded-2xl border`}
                >
                  <div className={`w-2 h-2 md:w-3 md:h-3 rounded-full bg-gradient-to-r ${selectedTechnique.color.primary} flex-shrink-0`} />
                  <span className={`${isDark ? 'text-gray-200' : 'text-gray-700'} text-xs md:text-base leading-tight`}>{benefit}</span>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
      
      {/* Settings Modal */}
      <AnimatePresence>
        {showSettings && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-3 sm:p-4"
            onClick={() => setShowSettings(false)}
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              className={`${isDark ? 'bg-gray-800 border-gray-600/30' : 'bg-white border-gray-200/50'} border rounded-xl sm:rounded-2xl md:rounded-3xl p-4 sm:p-6 md:p-8 max-w-xs sm:max-w-md w-full mx-3 sm:mx-4`}
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className={`text-lg sm:text-xl md:text-2xl font-bold ${isDark ? 'text-gray-100' : 'text-gray-800'} mb-3 sm:mb-4 md:mb-6`}>Ayarlar</h3>
              
              <div className="space-y-3 sm:space-y-4 md:space-y-6">
                <div>
                  <label className={`block ${isDark ? 'text-gray-300' : 'text-gray-600'} mb-2 text-xs sm:text-sm md:text-base`}>Seans Süresi (dakika)</label>
                  <input
                    type="range"
                    min="1"
                    max="30"
                    value={customDuration}
                    onChange={(e) => setCustomDuration(Number(e.target.value))}
                    className="w-full accent-blue-500"
                  />
                  <div className={`text-center ${isDark ? 'text-gray-100' : 'text-gray-800'} mt-2 text-xs sm:text-sm md:text-base`}>{customDuration} dakika</div>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className={`${isDark ? 'text-gray-300' : 'text-gray-600'} text-xs sm:text-sm md:text-base`}>Ses Efektleri</span>
                  <button
                    onClick={() => setSoundEnabled(!soundEnabled)}
                    className={`w-10 h-5 sm:w-12 sm:h-6 rounded-full transition-colors ${
                      soundEnabled ? 'bg-green-500' : 'bg-gray-600'
                    }`}
                  >
                    <div className={`w-4 h-4 sm:w-5 sm:h-5 bg-white rounded-full transition-transform ${
                      soundEnabled ? 'translate-x-5 sm:translate-x-6' : 'translate-x-0.5 sm:translate-x-1'
                    }`} />
                  </button>
                </div>
              </div>
              
              <button
                onClick={() => setShowSettings(false)}
                className="w-full mt-3 sm:mt-4 md:mt-6 py-2.5 sm:py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg sm:rounded-xl md:rounded-2xl font-semibold hover:shadow-lg transition-all duration-200 text-xs sm:text-sm md:text-base"
              >
                Tamam
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Info Modal */}
      <AnimatePresence>
        {showInfo && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-3 sm:p-4"
            onClick={() => setShowInfo(false)}
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              className={`${isDark ? 'bg-gray-800 border-gray-600/30' : 'bg-white border-gray-200/50'} border rounded-xl sm:rounded-2xl md:rounded-3xl p-3 sm:p-4 md:p-8 max-w-sm sm:max-w-lg md:max-w-3xl w-full max-h-[85vh] sm:max-h-[80vh] overflow-y-auto mx-auto scrollbar-hide`}
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className={`text-lg sm:text-xl md:text-2xl font-bold ${isDark ? 'text-gray-100' : 'text-gray-800'} mb-3 sm:mb-4 md:mb-6`}>{selectedTechnique.name} Hakkında</h3>
              
              <div className={`space-y-3 sm:space-y-4 md:space-y-6 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                <div className={`${isDark ? 'bg-blue-900/30 border-blue-600/30' : 'bg-blue-50/80 border-blue-200/50'} p-2 sm:p-3 md:p-4 rounded-lg sm:rounded-xl border`}>
                  <p className={`${isDark ? 'text-gray-200' : 'text-gray-700'} font-medium text-xs sm:text-sm md:text-base`}>{selectedTechnique.description}</p>
                </div>
                
                <div>
                  <h4 className={`font-semibold ${isDark ? 'text-gray-100' : 'text-gray-800'} mb-3 text-base md:text-lg`}>🔬 Bilimsel Dayanaklar:</h4>
                  <div className={`${isDark ? 'bg-green-900/30 border-green-600/30' : 'bg-green-50/80 border-green-200/50'} p-3 md:p-4 rounded-xl border space-y-2`}>
                    {selectedTechnique.benefits.map((benefit, index) => (
                      <div key={index} className="flex items-start space-x-2">
                        <span className={`${isDark ? 'text-green-400' : 'text-green-600'} font-bold flex-shrink-0`}>✓</span>
                        <span className={`${isDark ? 'text-gray-300' : 'text-gray-700'} text-sm md:text-base`}>{benefit}</span>
                      </div>
                    ))}
                  </div>
                </div>
                
                <div>
                  <h4 className={`font-semibold ${isDark ? 'text-gray-100' : 'text-gray-800'} mb-2 sm:mb-3 text-sm sm:text-base md:text-lg`}>📋 Nasıl Yapılır:</h4>
                  <div className={`${isDark ? 'bg-gray-700/50 border-gray-600/30' : 'bg-gray-50/80 border-gray-200/50'} p-2 sm:p-3 md:p-4 rounded-lg sm:rounded-xl border`}>
                    <ul className="space-y-1.5 sm:space-y-2">
                      <li className="flex items-center space-x-2">
                        <span className="w-5 h-5 sm:w-6 sm:h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-xs sm:text-sm font-bold flex-shrink-0">1</span>
                        <span className={`text-xs sm:text-sm md:text-base ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Nefes Al: <strong>{selectedTechnique.phases.inhale} saniye</strong></span>
                      </li>
                      {selectedTechnique.phases.hold1 && (
                        <li className="flex items-center space-x-2">
                          <span className="w-5 h-5 sm:w-6 sm:h-6 bg-yellow-500 text-white rounded-full flex items-center justify-center text-xs sm:text-sm font-bold flex-shrink-0">2</span>
                          <span className={`text-xs sm:text-sm md:text-base ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Nefesi Tut: <strong>{selectedTechnique.phases.hold1} saniye</strong></span>
                        </li>
                      )}
                      <li className="flex items-center space-x-2">
                        <span className="w-5 h-5 sm:w-6 sm:h-6 bg-red-500 text-white rounded-full flex items-center justify-center text-xs sm:text-sm font-bold flex-shrink-0">{selectedTechnique.phases.hold1 ? '3' : '2'}</span>
                        <span className={`text-xs sm:text-sm md:text-base ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Nefes Ver: <strong>{selectedTechnique.phases.exhale} saniye</strong></span>
                      </li>
                      {selectedTechnique.phases.hold2 && (
                        <li className="flex items-center space-x-2">
                          <span className="w-5 h-5 sm:w-6 sm:h-6 bg-purple-500 text-white rounded-full flex items-center justify-center text-xs sm:text-sm font-bold flex-shrink-0">4</span>
                          <span className={`text-xs sm:text-sm md:text-base ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Bekle: <strong>{selectedTechnique.phases.hold2} saniye</strong></span>
                        </li>
                      )}
                    </ul>
                  </div>
                </div>
                
                <div>
                  <h4 className={`font-semibold ${isDark ? 'text-gray-100' : 'text-gray-800'} mb-2 sm:mb-3 text-sm sm:text-base md:text-lg`}>⚠️ Önemli Notlar:</h4>
                  <div className={`${isDark ? 'bg-yellow-900/30 border-yellow-600/30' : 'bg-yellow-50/80 border-yellow-200/50'} p-2 sm:p-3 md:p-4 rounded-lg sm:rounded-xl border`}>
                    <ul className={`space-y-1 sm:space-y-1.5 text-xs sm:text-sm md:text-base ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                      <li className="flex items-start gap-1.5">
                        <span className="text-yellow-500 mt-0.5 flex-shrink-0">•</span>
                        <span>Rahat bir pozisyonda oturun veya uzanın</span>
                      </li>
                      <li className="flex items-start gap-1.5">
                        <span className="text-yellow-500 mt-0.5 flex-shrink-0">•</span>
                        <span>Zorlanmayın, doğal ritminizi bulun</span>
                      </li>
                      <li className="flex items-start gap-1.5">
                        <span className="text-yellow-500 mt-0.5 flex-shrink-0">•</span>
                        <span>Baş dönmesi hissederseniz normal nefes alın</span>
                      </li>
                      <li className="flex items-start gap-1.5">
                        <span className="text-yellow-500 mt-0.5 flex-shrink-0">•</span>
                        <span>Düzenli pratik yaparak faydaları artırın</span>
                      </li>
                      <li className="flex items-start gap-1.5">
                        <span className="text-yellow-500 mt-0.5 flex-shrink-0">•</span>
                        <span>Sağlık sorununuz varsa doktorunuza danışın</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
              
              <button
                onClick={() => setShowInfo(false)}
                className="w-full mt-4 md:mt-6 py-3 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-xl md:rounded-2xl font-semibold hover:shadow-lg transition-all duration-200 text-sm md:text-base"
              >
                Anladım
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default BreathingExercises